import type * as THREE from 'three'
import type {
  CameraPatchOptions,
  CameraStatePatch,
  CameraTransitionOptions,
} from '@/shared/types/animation'
import type { CameraState } from '@/shared/types/core'
import type { CameraRig, FitOptions, ViewPreset } from '@/shared/types/services'

/**
 * 代理型 CameraRig，将调用转发至当前活动视图的 CameraRig。
 */
export class ActiveViewCameraRigFacade implements CameraRig {
  constructor(private readonly getActiveRig: () => CameraRig) {}

  /** 当前相机模式（Perspective/Orthographic） */
  get mode() {
    return this.getActiveRig().mode
  }

  /** 当前相机位置，写入将覆盖活动 rig */
  get position(): THREE.Vector3 {
    return this.getActiveRig().position
  }

  set position(value: THREE.Vector3) {
    this.getActiveRig().position = value
  }

  /** 当前视线目标，写入将覆盖活动 rig */
  get target(): THREE.Vector3 {
    return this.getActiveRig().target
  }

  set target(value: THREE.Vector3) {
    this.getActiveRig().target = value
  }

  /** 当前 up 向量 */
  get up(): THREE.Vector3 {
    return this.getActiveRig().up
  }

  /** 当前透视 FOV，单位度 */
  get fov(): number {
    return this.getActiveRig().fov
  }

  set fov(value: number) {
    this.getActiveRig().fov = value
  }

  /** 当前正交缩放 */
  get zoom(): number {
    return this.getActiveRig().zoom
  }

  set zoom(value: number) {
    this.getActiveRig().zoom = value
  }

  /** 当前近裁剪距离 */
  get near(): number {
    return this.getActiveRig().near
  }

  /** 当前远裁剪距离 */
  get far(): number {
    return this.getActiveRig().far
  }

  /** 当前相机绕视线的 roll 角，单位弧度 */
  get roll(): number {
    return this.getActiveRig().roll
  }

  set roll(value: number) {
    this.getActiveRig().roll = value
  }

  get isTransitioning(): boolean {
    return this.getActiveRig().isTransitioning
  }

  /**
   * 切换相机模式。
   *
   * @param mode - 目标相机模式
   */
  setMode(mode: CameraState['mode']): void {
    this.getActiveRig().setMode(mode)
  }

  /**
   * 将相机设为俯视指定平面。
   *
   * @param origin - 平面原点
   * @param normal - 平面法线
   * @param up - 相机 up 方向
   * @param distance - 相机到平面的距离，单位米
   */
  setTopDownOnPlane(
    origin: THREE.Vector3,
    normal: THREE.Vector3,
    up: THREE.Vector3,
    distance: number
  ): void {
    this.getActiveRig().setTopDownOnPlane(origin, normal, up, distance)
  }

  /**
   * 根据场景包围盒自动更新近远裁剪。
   *
   * @param sceneBounds - 场景包围盒
   */
  updateNearFar(sceneBounds: THREE.Box3): void {
    this.getActiveRig().updateNearFar(sceneBounds)
  }

  /**
   * 将相机移动到恰好覆盖指定 AABB 的位置。
   *
   * @param box - 目标 AABB
   * @param options - 额外拟合选项
   */
  fitToBox(box: THREE.Box3, options?: FitOptions): void {
    this.getActiveRig().fitToBox(box, options)
  }

  /**
   * 应用预设视角（前/后/左/右/顶/底/等轴等）。
   *
   * @param preset - 目标视角
   */
  setPresetView(preset: ViewPreset): void {
    this.getActiveRig().setPresetView(preset)
  }

  /**
   * 以平滑动画切换到目标相机状态。
   *
   * @param state - 目标状态增量
   * @param duration - 动画时长，单位毫秒
   * @returns 动画完成的 Promise
   */
  animateTo(
    state: Partial<CameraState>,
    duration?: number | CameraTransitionOptions,
    options?: CameraTransitionOptions
  ): Promise<void> {
    return this.getActiveRig().animateTo(state, duration, options)
  }

  /**
   * 批量应用相机状态补丁。
   *
   * @param patch - 只包含需要更新的相机字段
   * @param options - 应用选项
   */
  applyStatePatch(patch: CameraStatePatch, options?: CameraPatchOptions): void {
    this.getActiveRig().applyStatePatch(patch, options)
  }

  /** 取消当前一次性相机过渡动画。 */
  cancelTransition(): void {
    this.getActiveRig().cancelTransition()
  }

  /** 当前活动视图是否存在一次性相机过渡。底层 rig 未实现时按 false 处理。 */
  /**
   * 获取相机完整状态快照。
   *
   * @returns 完整 CameraState
   */
  getState(): CameraState {
    return this.getActiveRig().getState()
  }

  /**
   * 设置相机完整状态。
   *
   * @param state - 完整 CameraState
   */
  setState(state: CameraState): void {
    this.getActiveRig().setState(state)
  }

  /** 释放代理（空实现，具体 rig 自行释放） */
  dispose(): void {}
}
